export type BackendUser = {
  id?: string;
  nombre?: string;
  correo: string;
  role?: string;
};

const AUTH_TOKEN_KEY = "medam_careers_auth_token";
const AUTH_USER_KEY = "medam_careers_auth_user";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const TOKEN_RE = /^[A-Za-z0-9._~+/=-]+$/;

const normalizeEmailInput = (value: string) => String(value ?? "").trim().toLowerCase();

const sanitizeToken = (token?: string | null) => {
  const raw = typeof token === "string" ? token.trim() : "";
  return raw && raw.length >= 20 && TOKEN_RE.test(raw) ? raw : null;
};

const isValidUser = (user: Partial<BackendUser> | null | undefined): user is BackendUser => {
  if (!user || typeof user !== "object") return false;
  if (!user.correo || !EMAIL_RE.test(String(user.correo).trim())) return false;
  return typeof user.id === "undefined" || typeof user.id === "string";
};

const normalizeBaseUrl = (value?: string, suffix?: string) => {
  const raw = (value || "https://server-medam-f223735b9151.herokuapp.com").replace(/\/+$/, "");
  if (!suffix) return raw;
  return raw.endsWith(suffix) ? raw : `${raw}${suffix}`;
};

export const resolveCareersBaseUrl = () => {
  const raw =
    (typeof window !== "undefined" ? import.meta.env.VITE_MEDAM_CAREERS_API_URL : process.env.MEDAM_CAREERS_API_URL) ||
    "https://server-medam-f223735b9151.herokuapp.com/medam_careers";
  return normalizeBaseUrl(raw, "/medam_careers");
};

export const resolveServerBaseUrl = () => {
  const raw =
    (typeof window !== "undefined" ? import.meta.env.VITE_MEDAM_CAREERS_API_URL : process.env.MEDAM_CAREERS_API_URL) ||
    "https://server-medam-f223735b9151.herokuapp.com/medam_careers";
  return normalizeBaseUrl(raw.replace(/\/medam_careers\/?$/, ""), "");
};

export const resolveBaseUrl = resolveCareersBaseUrl;

export const getStoredAuthToken = () => {
  if (typeof window === "undefined") return null;
  const token = sanitizeToken(window.localStorage.getItem(AUTH_TOKEN_KEY));
  if (!token) {
    clearAuthSession();
    return null;
  }
  return token;
};

export const getStoredAuthUser = (): BackendUser | null => {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(AUTH_USER_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<BackendUser>;
    return isValidUser(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const saveAuthSession = (token: string, user: BackendUser) => {
  if (typeof window === "undefined") return;
  const normalizedToken = sanitizeToken(token);
  if (!normalizedToken || !isValidUser(user)) {
    throw new Error("La sesión recibida del backend es inválida.");
  }
  window.localStorage.setItem(AUTH_TOKEN_KEY, normalizedToken);
  window.localStorage.setItem(AUTH_USER_KEY, JSON.stringify({ ...user, correo: normalizeEmailInput(user.correo) }));
};

export const clearAuthSession = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
  window.localStorage.removeItem(AUTH_USER_KEY);
};

export const buildAuthHeader = (token?: string | null) => {
  const value = sanitizeToken(token ?? getStoredAuthToken());
  return value ? `Bearer ${value}` : null;
};

export const loginWithBackend = async (email: string, password: string) => {
  const normalizedEmail = normalizeEmailInput(email);
  const normalizedPassword = String(password ?? "").trim();

  if (!EMAIL_RE.test(normalizedEmail)) {
    throw new Error("Ingresa un correo electrónico válido.");
  }
  if (normalizedPassword.length < 8) {
    throw new Error("La contraseña debe tener al menos 8 caracteres.");
  }

  const response = await fetch(`${resolveServerBaseUrl()}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email: normalizedEmail, password: normalizedPassword }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.message || "No se pudo iniciar sesión.");
  }

  if (!payload?.token || !payload?.user) {
    throw new Error("La respuesta del backend no incluye token de sesión.");
  }

  saveAuthSession(payload.token, payload.user);
  return payload as { token: string; user: BackendUser };
};

export const registerAdminWithBackend = async (email: string, password: string, nombre?: string) => {
  const normalizedEmail = normalizeEmailInput(email);
  const normalizedPassword = String(password ?? "").trim();
  const normalizedNombre = String(nombre ?? "").trim();

  if (!EMAIL_RE.test(normalizedEmail)) {
    throw new Error("Ingresa un correo electrónico válido.");
  }
  if (normalizedPassword.length < 8) {
    throw new Error("La contraseña debe tener al menos 8 caracteres.");
  }
  if (normalizedNombre.length > 120) {
    throw new Error("El nombre es demasiado largo.");
  }

  const response = await fetch(`${resolveServerBaseUrl()}/auth/register-admin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: normalizedEmail,
      password: normalizedPassword,
      nombre: normalizedNombre || undefined,
    }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.message || "No se pudo crear la cuenta administrativa.");
  }

  if (!payload?.token || !payload?.user) {
    throw new Error("La respuesta del backend no incluye usuario autenticado.");
  }

  saveAuthSession(payload.token, payload.user);
  return payload as { token: string; user: BackendUser };
};
